import type { ReactNode } from 'react';
import { Info } from 'lucide-react';

/**
 * The small layout pieces of the hiring windows of 15 Oct 2026: a form row (a grey label of
 * two to four words on the left, the control on the right), the two-button segmented
 * choice, the one (i), and a label-and-value list. Nothing here calls the server.
 */

/** One row of a form: the label (with "optional" under it when it is), then the control. */
export function FormRow({ label, optional, info, htmlFor, labelId, children }: {
  label: string;
  /** Drawn under the label in small grey words. */
  optional?: string;
  /** The row's one (i). */
  info?: string;
  htmlFor?: string;
  labelId?: string;
  children: ReactNode;
}) {
  const text = (
    <>
      <span className="inline-flex items-center gap-1.5">
        {label}
        {info && <InfoDot text={info} />}
      </span>
      {optional && <span className="block text-xs font-normal leading-tight text-gray-400">{optional}</span>}
    </>
  );
  return (
    <div className="grid grid-cols-1 gap-x-3 gap-y-1 py-1.5 sm:grid-cols-[110px_minmax(0,1fr)] sm:items-start">
      {htmlFor ? (
        <label id={labelId} htmlFor={htmlFor} className="pt-2 text-[13px] font-medium text-gray-600">{text}</label>
      ) : (
        <div id={labelId} className="pt-2 text-[13px] font-medium text-gray-600">{text}</div>
      )}
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** The one (i): its words on hover, and to a screen reader. */
export function InfoDot({ text, label }: { text: string; label?: string }) {
  return (
    <span
      role="img"
      aria-label={label ? `${label}: ${text}` : text}
      title={text}
      className="inline-flex flex-none cursor-help items-center text-gray-400"
    >
      <Info className="h-[15px] w-[15px]" aria-hidden="true" />
    </span>
  );
}

/** Two (or a few) buttons side by side, one chosen: a radio group in the look of a segmented control. */
export function Segmented<T extends string>({ label, labelledBy, options, value, onChange, disabled = false }: {
  label?: string;
  labelledBy?: string;
  options: readonly { value: T; label: string; hover?: string | null }[];
  value: T | '';
  onChange: (value: T) => void;
  disabled?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      className="inline-flex overflow-hidden rounded-lg border border-gray-300 bg-white"
    >
      {options.map((option, i) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={on}
            title={option.hover ?? undefined}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={`whitespace-nowrap px-3.5 py-[7px] text-[13.5px] transition-colors disabled:opacity-60 ${
              i > 0 ? 'border-l border-gray-300' : ''
            } ${on ? 'bg-primary-50 font-semibold text-primary-800 ring-1 ring-inset ring-primary-600' : 'text-gray-700 hover:bg-gray-50'}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** A label-and-value list, the label column grey. A value of null is left out. */
export function KeyValues({ rows, label, labelWidth = 'sm:grid-cols-[110px_minmax(0,1fr)]' }: {
  rows: ReadonlyArray<{ key: string; label: string; value: ReactNode | null }>;
  label?: string;
  labelWidth?: string;
}) {
  return (
    <dl aria-label={label} className={`grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:gap-y-3 ${labelWidth}`}>
      {rows.filter((row) => row.value != null && row.value !== '').map((row) => (
        <div key={row.key} className="contents">
          <dt className="text-gray-500">{row.label}</dt>
          <dd className="mb-2 min-w-0 break-words text-gray-900 sm:mb-0">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A small sub-heading inside a form, with a line above it. */
export function FormSubheading({ children, note }: { children: ReactNode; note?: string | null }) {
  return (
    <div className="mb-0.5 mt-2 border-t border-gray-200 pt-3">
      <h4 className="text-[13px] font-semibold text-gray-900">{children}</h4>
      {note && <p className="text-xs text-gray-500">{note}</p>}
    </div>
  );
}
